import { useEffect, useRef, useState } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import {
  UpdatingModalProps,
  UpdatingModalVariant,
} from '@components/UpdatingModal';
import {
  ControlGivenEvent,
  ControlRequestedEvent,
} from '@generated/gql/graphql';

interface UseControlFeedbackModalsArgs {
  controlRequest?: ControlRequestedEvent | null;
  controlGiven?: ControlGivenEvent | null;
  hasControl: boolean | null;
  userId?: string | null;
}

const useControlFeedbackModals = ({
  controlRequest,
  controlGiven,
  hasControl,
  userId,
}: UseControlFeedbackModalsArgs) => {
  const controlRequestDialog = useDialog(DialogID.APMT_CONTROL_REQUEST_LOADING);
  const givingControlDialog = useDialog(DialogID.APMT_GIVING_CONTROL);

  const [dialogProps, setDialogProps] = useState<UpdatingModalProps>({
    heading: '',
  });

  const autoCloseRef = useRef<number | null>(null);

  useEffect(() => {
    if (!userId) return;

    if (controlRequest?.fromParticipant.id === userId && !hasControl) {
      setDialogProps({
        id: DialogID.APMT_CONTROL_REQUEST_LOADING,
        variant: UpdatingModalVariant.TEXT_ABOVE,
        heading: `Requesting screen control from ${controlRequest.toParticipant.name}...`,
      });
      controlRequestDialog.setOpen(true);
    } else if (hasControl || !controlRequest) {
      controlRequestDialog.setOpen(false);
    }
  }, [controlRequest, hasControl, userId, controlRequestDialog]);

  useEffect(() => {
    if (!controlGiven || !userId) return;

    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
    }

    if (controlGiven.fromParticipant.id === userId) {
      setDialogProps({
        id: DialogID.APMT_GIVING_CONTROL,
        variant: UpdatingModalVariant.TEXT_ABOVE,
        heading: `Giving screen control to ${controlGiven.toParticipant.name}...`,
      });
      givingControlDialog.setOpen(true);
    } else if (controlGiven.toParticipant.id === userId) {
      setDialogProps({
        id: DialogID.APMT_GIVING_CONTROL,
        variant: UpdatingModalVariant.TEXT_ABOVE,
        heading: `${controlGiven.fromParticipant.name} is giving you screen control...`,
      });
      givingControlDialog.setOpen(true);
    } else {
      return;
    }

    autoCloseRef.current = window.setTimeout(() => {
      givingControlDialog.setOpen(false);
    }, 1500);

    return () => {
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlGiven, userId]);

  return { dialogProps };
};
export { useControlFeedbackModals };
